import { Component, OnInit, signal, inject, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { NotificationApiService, NotificationModel } from '../../../services/notification-api.service';
import { NotificationService } from '../../../services/notification.service';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <div class="notification-panel">
      <div class="notification-header">
        <div class="header-left">
          <h3>Notifications</h3>
          @if (notifications().length > 0) {
            <span class="notification-count-badge">{{ notifications().length }}</span>
          }
        </div>
        <div class="header-right">
          @if (notifications().length > 0) {
            <button pButton label="Mark All as Read" class="p-button-text p-button-sm mark-all-button"
                    (click)="markAllAsRead()"></button>
          }
          <button pButton icon="pi pi-times" class="p-button-text p-button-rounded p-button-sm close-button"
                  (click)="closePanel.emit()"></button>
        </div>
      </div>
      <div class="notification-list">
        @if (loading()) {
          <div class="notification-loading">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading notifications...</span>
          </div>
        } @else if (notifications().length === 0) {
          <div class="notification-empty">
            <i class="pi pi-bell"></i>
            <span>No unread notifications</span>
          </div>
        } @else {
          @for (notification of notifications(); track notification.notificationId) {
            <div class="notification-item" (click)="markAsRead(notification)">
              <div class="notification-icon" [class]="'notification-icon-' + notification.type">
                <i [class]="getIconClass(notification.type)"></i>
              </div>
              <div class="notification-content">
                <div class="notification-title">{{ notification.title }}</div>
                <div class="notification-message">{{ notification.message }}</div>
                <div class="notification-time">{{ getRelativeTime(notification.createdAt) }}</div>
              </div>
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: [`
    .notification-panel {
      width: 380px;
      max-height: 500px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
    }

    .notification-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e7eb;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .notification-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #111827;
    }

    .notification-count-badge {
      background-color: #ef4444;
      color: white;
      border-radius: 10px;
      padding: 2px 8px;
      font-size: 12px;
      font-weight: 600;
      min-width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mark-all-button {
      color: #3b82f6 !important;
      font-size: 13px !important;
      padding: 4px 8px !important;
    }

    .mark-all-button:hover {
      color: #2563eb !important;
      background-color: #eff6ff !important;
    }

    .close-button {
      color: #6b7280 !important;
    }

    .close-button:hover {
      color: #111827 !important;
    }

    .notification-list {
      overflow-y: auto;
      max-height: 440px;
    }

    .notification-loading,
    .notification-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      color: #6b7280;
      gap: 12px;
    }

    .notification-loading i,
    .notification-empty i {
      font-size: 32px;
    }

    .notification-item {
      display: flex;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid #f3f4f6;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .notification-item:hover {
      background-color: #f9fafb;
    }

    .notification-icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .notification-icon-info {
      background-color: #dbeafe;
      color: #1e40af;
    }

    .notification-icon-success {
      background-color: #d1fae5;
      color: #065f46;
    }

    .notification-icon-warning {
      background-color: #fef3c7;
      color: #92400e;
    }

    .notification-icon-error {
      background-color: #fee2e2;
      color: #991b1b;
    }

    .notification-content {
      flex: 1;
      min-width: 0;
    }

    .notification-title {
      font-weight: 600;
      font-size: 14px;
      color: #111827;
      margin-bottom: 4px;
    }

    .notification-message {
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 4px;
      line-height: 1.4;
    }

    .notification-time {
      font-size: 12px;
      color: #9ca3af;
    }
  `]
})
export class NotificationPanel implements OnInit {
  private notificationApiService = inject(NotificationApiService);
  private notificationService = inject(NotificationService);

  @Output() closePanel = new EventEmitter<void>();

  notifications = signal<NotificationModel[]>([]);
  loading = signal(false);

  ngOnInit(): void {
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.loading.set(true);
    this.notificationApiService.getUnreadNotifications().subscribe({
      next: (notifications) => {
        this.notifications.set(notifications);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading notifications:', error);
        this.loading.set(false);
        this.notificationService.error('Error', 'Failed to load notifications');
      }
    });
  }

  markAsRead(notification: NotificationModel): void {
    this.notificationApiService.markAsRead(notification.notificationId).subscribe({
      next: () => {
        this.notifications.update(notifications =>
          notifications.filter(n => n.notificationId !== notification.notificationId)
        );
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
      }
    });
  }

  markAllAsRead(): void {
    this.notificationApiService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.set([]);
        this.notificationService.show({
          type: 'success',
          title: 'Success',
          message: 'All notifications marked as read'
        });
      },
      error: (error) => {
        console.error('Error marking all notifications as read:', error);
        this.notificationService.error('Error', 'Failed to mark all notifications as read');
      }
    });
  }

  getIconClass(type: string): string {
    const iconMap: { [key: string]: string } = {
      info: 'pi pi-info-circle',
      success: 'pi pi-check-circle',
      warning: 'pi pi-exclamation-triangle',
      error: 'pi pi-times-circle'
    };
    return iconMap[type] || 'pi pi-info-circle';
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  }
}
